import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ArrowRight, Award, BarChart3, CheckCircle2, ChevronDown, ChevronUp, Clock, Download, Loader2, Users } from 'lucide-react';
import { haalBeroepsregistratie, type NormMeter } from '../../services/professionalDataApi';
import { downloadCertificaat, foutmelding, verificatieUrl } from '../../services/beroepsregistratieApi';
import { Kaart, Laden } from '../data/DataFilterBalk';
import { KLEUR, Meter } from '../data/DataCharts';
import { HorizontaleBalken } from './Grafieken';
import { FilterRij, Fout, Keuze, datumNl, getal } from './Bediening';
import { BeroepsregistratieCertificaatKaart } from '../BeroepsregistratieCertificaatKaart';
import { ComplianceKeurmerkKaart } from '../ComplianceKeurmerkKaart';

const isoDag = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const NormTegel: React.FC<{
  icoon: React.ReactNode;
  label: string;
  bron: string;
  meter: NormMeter;
  eenheid: string;
  restantTekst: string;
  extra?: React.ReactNode;
}> = ({ icoon, label, bron, meter, eenheid, restantTekst, extra }) => {
  const { t } = useTranslation('professionalData');
  return (
    <div className="flex flex-col rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2 text-navy-900">
        {icoon}
        <span className="text-xs font-semibold uppercase tracking-wide">{label}</span>
      </div>
      <p className="mt-2 text-2xl font-bold text-gray-900">
        {t('beroepsregistratie.van', { behaald: getal(meter.behaald), norm: getal(meter.norm) })}
        <span className="ml-1 text-sm font-medium text-gray-500">{eenheid}</span>
      </p>
      <div className="mt-2">
        <Meter waarde={meter.behaald} max={meter.norm} kleur={meter.gehaald ? '#008300' : KLEUR.blauw} label={label} />
      </div>
      <p className="mt-1 text-xs text-gray-500">{bron}</p>
      {extra && <div className="mt-1 text-xs text-gray-500">{extra}</div>}
      <p className={`mt-auto flex items-start gap-1.5 pt-3 text-xs font-medium ${meter.gehaald ? 'text-green-800' : 'text-amber-800'}`}>
        {meter.gehaald ? <CheckCircle2 className="mt-px h-3.5 w-3.5 flex-shrink-0" aria-hidden /> : <AlertTriangle className="mt-px h-3.5 w-3.5 flex-shrink-0" aria-hidden />}
        {meter.gehaald ? t('beroepsregistratie.gehaald') : restantTekst}
      </p>
    </div>
  );
};

export const BeroepsregistratieOnderdeel: React.FC = () => {
  const { t, i18n } = useTranslation('professionalData');
  const datum = (iso: string) => datumNl(iso, i18n.language);
  const [keuze, setKeuze] = useState<'huidig' | 'komend'>('huidig');
  const [toonLedger, setToonLedger] = useState(false);
  const [hash, setHash] = useState<string | null>(null);
  const [exportFout, setExportFout] = useState<string | null>(null);
  const [gekopieerd, setGekopieerd] = useState(false);
  const jaar = new Date().getFullYear();

  const q = useQuery({ queryKey: ['professional-data', 'beroepsregistratie', keuze], queryFn: () => haalBeroepsregistratie(keuze), staleTime: 60_000 });
  const d = q.data;

  const exportTot = d ? (isoDag(new Date()) < d.periode.tot ? isoDag(new Date()) : d.periode.tot) : '';
  const exporteer = useMutation({
    mutationFn: () => downloadCertificaat(d!.periode.van, exportTot, 'pdf'),
    onMutate: () => {
      setExportFout(null);
      setHash(null);
    },
    onSuccess: (r) => setHash(r.hash),
    onError: async (e) => setExportFout(await foutmelding(e, t('fout'))),
  });

  const kopieer = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setGekopieerd(true);
      setTimeout(() => setGekopieerd(false), 2000);
    } catch {
      window.prompt(t('beroepsregistratie.kopieer'), url);
    }
  };

  return (
    <div className="space-y-6">
      <FilterRij>
        <Keuze
          label={t('beroepsregistratie.periode')}
          waarde={keuze}
          onChange={(v) => {
            setKeuze(v);
            setHash(null);
          }}
          opties={[
            { waarde: 'huidig', label: t('beroepsregistratie.huidig', { jaar }) },
            { waarde: 'komend', label: t('beroepsregistratie.komend', { jaar: jaar + 1 }) },
          ]}
        />
        {d && <p className="pb-2 text-sm text-gray-600">{t('beroepsregistratie.deadline', { datum: datum(d.deadline) })}</p>}
      </FilterRij>

      {q.isLoading && <Laden />}
      {q.isError && <Fout fout={q.error} />}
      {d && (
        <>
          <div className="grid gap-4 md:grid-cols-3">
            <NormTegel
              icoon={<Award className="h-4 w-4" />}
              label={t('beroepsregistratie.pe')}
              bron={t('beroepsregistratie.peBron')}
              meter={d.pe_punten}
              eenheid={t('beroepsregistratie.eenheidPe')}
              restantTekst={t('beroepsregistratie.restantPe', { restant: getal(d.pe_punten.restant) })}
              extra={d.pe_punten.per_register.length > 0 && d.pe_punten.per_register.map((r) => `${r.register}: ${getal(r.punten)}`).join(' · ')}
            />
            <NormTegel
              icoon={<Users className="h-4 w-4" />}
              label={t('beroepsregistratie.intervisie')}
              bron={t('beroepsregistratie.intervisieBron')}
              meter={d.intervisie}
              eenheid={t('beroepsregistratie.eenheidUren')}
              restantTekst={t('beroepsregistratie.restantIntervisie', { restant: getal(d.intervisie.restant) })}
              extra={
                <>
                  {t('beroepsregistratie.sessies', { count: d.intervisie.sessies })}
                  {d.intervisie.afgewezen > 0 && <span className="block text-amber-800">{t('beroepsregistratie.afgewezen', { count: d.intervisie.afgewezen })}</span>}
                </>
              }
            />
            <NormTegel
              icoon={<Clock className="h-4 w-4" />}
              label={t('beroepsregistratie.praktijk')}
              bron={t('beroepsregistratie.praktijkBron')}
              meter={d.praktijk}
              eenheid={t('beroepsregistratie.eenheidUren')}
              restantTekst={t('beroepsregistratie.restantPraktijk', { restant: getal(d.praktijk.restant) })}
              extra={t('beroepsregistratie.facturen', { count: d.praktijk.facturen })}
            />
          </div>

          <div className="rounded-xl border border-navy-100 bg-navy-50/40 p-4">
            <button
              type="button"
              disabled={keuze === 'komend' || exporteer.isPending}
              onClick={() => exporteer.mutate()}
              className="inline-flex items-center gap-2 rounded-lg bg-navy-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-navy-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {exporteer.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              {exporteer.isPending ? t('beroepsregistratie.exportBezig') : t('beroepsregistratie.export')}
            </button>
            <p className="mt-2 text-xs text-gray-600">
              {keuze === 'komend' ? t('beroepsregistratie.exportKomend') : t('beroepsregistratie.exportUitleg', { van: datum(d.periode.van), tot: datum(exportTot) })}
            </p>
            {exportFout && <p className="mt-2 text-sm text-red-700">{exportFout}</p>}
            {hash && (
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-700">
                <span>{t('beroepsregistratie.exportKlaar')}</span>
                <a href={verificatieUrl(hash)} target="_blank" rel="noopener noreferrer" className="break-all font-mono text-navy-800 underline">
                  {verificatieUrl(hash)}
                </a>
                <button type="button" onClick={() => kopieer(verificatieUrl(hash))} className="rounded border border-gray-300 bg-white px-2 py-0.5 font-medium hover:bg-gray-50">
                  {gekopieerd ? t('beroepsregistratie.gekopieerd') : t('beroepsregistratie.kopieer')}
                </button>
              </div>
            )}
          </div>

          <Kaart titel={t('beroepsregistratie.grafiekTitel')} uitleg={t('beroepsregistratie.grafiekUitleg')}>
            <HorizontaleBalken
              rijen={d.casussen_per_rol.map((r) => ({ key: r.rol, label: t(`beroepsregistratie.rol.${r.rol}`), waarde: r.casussen }))}
              formaat={(v) => String(Math.round(v))}
            />
            <p className="mt-3 text-xs text-gray-500">
              {t('beroepsregistratie.casussen')}: <strong className="text-gray-800">{d.casussen_per_rol.reduce((s, r) => s + r.casussen, 0)}</strong>
            </p>
          </Kaart>
        </>
      )}

      <Link to="/professional/jaarverslag" className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 transition hover:shadow-md">
        <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-navy-50">
          <BarChart3 className="h-5 w-5 text-navy-900" />
        </span>
        <span className="min-w-0 flex-1 font-semibold text-navy-900">{t('menu.caseRegistratie')}</span>
        <ArrowRight className="h-4 w-4 text-navy-400" />
      </Link>

      <section className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <button type="button" onClick={() => setToonLedger((v) => !v)} aria-expanded={toonLedger} className="flex w-full items-center justify-between gap-3 p-4 text-left">
          <span>
            <span className="block font-semibold text-gray-900">{t('beroepsregistratie.ledgerTitel')}</span>
            <span className="block text-xs text-gray-500">{t('beroepsregistratie.ledgerUitleg')}</span>
          </span>
          {toonLedger ? <ChevronUp className="h-4 w-4 text-gray-500" /> : <ChevronDown className="h-4 w-4 text-gray-500" />}
        </button>
        {toonLedger && (
          <div className="space-y-6 border-t border-gray-100 p-4">
            <ComplianceKeurmerkKaart />
            <BeroepsregistratieCertificaatKaart />
          </div>
        )}
      </section>
    </div>
  );
};
